import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { get, map, head, find, keys } from 'lodash'
import cn from 'classnames'
import styles from './Items.module.scss'
import useMenu from '../context/hooks/useMenu'
import { getFocusedItemId, getHasQuickSearchResults, getHighlightedItemsById, getSearchQuery } from '../selectors/search'
import { getItemsWithQuantity } from '../selectors/items'
import { getItemCategories } from '../selectors/itemCategory'
import { useSelector } from 'react-redux'
import VNMenuItem from '../VNItems/components/VNMenuItem'
import Item from './Item'

const MIN_ITEM_COUNT_MOBILE_THRESHOLD = 7

const Items = ({ isResourceRestrained = false }) => {
  let focusedItemId = useSelector(getFocusedItemId)
  const highlightedItemsById = useSelector(getHighlightedItemsById)
  const searchQuery = useSelector(getSearchQuery)

  const hasQuickSearchResults = useSelector(getHasQuickSearchResults)
  const items = useSelector(getItemsWithQuantity)
  //const items = useSelector(getItemsForMenu)
  const itemCategories = useSelector(getItemCategories)

  //const { upsertCartItem } = useItems()
  const itemsContainerRef = useRef()
  const hasMinItemCount = useMemo(() => items?.length >= MIN_ITEM_COUNT_MOBILE_THRESHOLD, [items])
  const classNames = cn(styles.items, { [styles.hasMinItemCount]: hasMinItemCount })
  const hasCartItems = useMemo(() => items?.some((item) => item?.cartQuantity > 0), [items])
  const { handleScroll, setScrollPositions, selectedCategory, setSelectedCategory, isMobile } = useMenu()

  if (!focusedItemId && selectedCategory.search) {
    focusedItemId = get(
      find(items, (item) => item.categoryId === selectedCategory.id),
      'id',
      undefined,
    )
  }

  //this is a temporary fix to an issue where this component is first mounted with a previous menu's items at first
  //which will affect the scrollPositions collected from categories if both menu's shared a category.
  useEffect(() => {
    setSelectedCategory({ id: head(keys(itemCategories)) || '', search: false, highlight: true })
    setScrollPositions({})

    return () => {
      setSelectedCategory({ id: '', search: false, highlight: false }) // removing highlighted category on unMount to avoid having a shared category highlighted in a different menu
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const categoryBreakPoints = useMemo(() => {
    const breakpoints = {}
    items.forEach((item) => {
      if (itemCategories[item.categoryId] && !breakpoints[item.categoryId]) {
        breakpoints[item.categoryId] = item.id
      }
    })
    return breakpoints
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isHighlighted = (searchQuery, hasQuickSearchResults, highlightedItemsById, item) => {
    if (!searchQuery) {
      return true
    }
    if (!hasQuickSearchResults) {
      return false
    }
    return !!get(highlightedItemsById, item.id, false)
  }

  const handleMenuScroll = useCallback(() => {
    if (!isMobile) return
    if (isResourceRestrained) return // TODO: [T38895] remove this when category navigation is redone

    handleScroll(itemsContainerRef)
  }, [handleScroll, isMobile, isResourceRestrained])

  return (
    <div className={classNames} ref={itemsContainerRef} onScroll={handleMenuScroll}>
      {map(items, (item, idx) => {
        // this should be done in the selector
        const highlighted = isHighlighted(searchQuery, hasQuickSearchResults, highlightedItemsById, item)
        const focusedItem = focusedItemId === item.id && item.categoryId === selectedCategory.id

        if (!highlighted) {
          return null
        }

        if (isResourceRestrained) {
          // TODO: [T38895] remove this when category navigation is redone
          return (
            <div className={styles.itemContainer} key={idx} data-id={item.categoryId}>
              <VNMenuItem
                cartIndex={item.cartIndex}
                max={item.orderMaxAmount}
                min={item.orderMinCount}
                name={item.name}
                id={item.id}
                color={item.color}
                price={item.price}
                isAlcohol={item.isAlcohol || false}
                cartQuantity={item.cartQuantity}
                cartQuantityWithModifiers={item.cartQuantityWithModifiers}
                hasRequiredModifiers={item.hasRequiredModifiers}
              />
            </div>
          )
        }

        return (
          <div className={styles.itemContainer} key={idx}>
            <Item
              {...item}
              key={item.id + item.categoryId}
              className={styles.item}
              highlighted={highlighted}
              focusedItem={focusedItem}
              categoryBreakPoints={categoryBreakPoints}
            />
          </div>
        )
      })}
      <div className={cn(styles.bottomPadding, { [styles.hasCartItems]: hasCartItems })}></div>
    </div>
  )
}

Items.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onTapItem: PropTypes.func,
  className: PropTypes.string,
  searchQuery: PropTypes.string,
  highlightedItemsById: PropTypes.object,
}

export default memo(Items)
